import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Edit } from 'lucide-react';

interface Profile {
  id: string;
  name: string;
  source: string;
  formId: string;
  isActive: boolean;
  createdAtUtc: string;
}

interface ProfileListProps {
  profiles: Profile[];
  selectedProfile: Profile | null;
  onSelectProfile: (profile: Profile) => void;
  onEditProfile: (profile: Profile) => void;
  onDeleteProfile: (id: string) => void;
  loading?: boolean;
}

export default function ProfileList({
  profiles,
  selectedProfile,
  onSelectProfile,
  onEditProfile,
  onDeleteProfile,
  loading,
}: ProfileListProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-card border rounded-lg overflow-hidden">
      <div className="p-4 border-b bg-muted/30">
        <h3 className="text-lg font-semibold">Profilok ({profiles.length})</h3>
      </div>

      <div className="divide-y max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : profiles.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t("mappingsExtra.noProfiles")}
          </div>
        ) : (
          profiles.map((profile) => (
            <div
              key={profile.id}
              onClick={() => onSelectProfile(profile)}
              className={`w-full text-left p-3 transition-colors cursor-pointer ${
                selectedProfile?.id === profile.id
                  ? 'bg-primary/10 border-l-2 border-primary'
                  : 'hover:bg-muted/50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{profile.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {profile.source === 'facebook'
                        ? t('profileModal.facebookLeadAds')
                        : profile.source}
                    </Badge>
                    {profile.isActive ? (
                      <Badge variant="default" className="text-xs">
                        Aktív
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Inaktív
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {profile.formId}
                  </p>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProfile(profile);
                    }}
                    title="Szerkesztés"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProfile(profile.id);
                    }}
                    title="Törlés"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
